import React, { useState } from 'react';
import FileUpload from '../components/FileUpload';
import DatabaseList from '../components/DatabaseList';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { Database, Plus, X, Info } from 'lucide-react';

interface DatabaseFormatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DatabaseFormatModal: React.FC<DatabaseFormatModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Format fichier pour importer des articles (CSV/XLSX/XLS)</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="space-y-6">
          {/* IMPORTANT: Message d'information en haut */}
          <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-amber-900 mb-2">Information importante</h4>
                <p className="text-sm text-amber-800 space-y-2">
                  <strong>La structure des colonnes est définie lors du premier import d'articles.</strong>
                  <br />
                  • Les colonnes d'articles que vous incluez lors du premier import déterminent la structure permanente de votre base.
                  <br />
                  • Les colonnes de ventes (format MMM_AA comme janv_22) seront automatiquement ignorées lors de l'import d'articles.
                  <br />
                  • Les imports ultérieurs d'articles devront respecter exactement cette structure (mêmes noms de colonnes sanitisés).
                  <br />
                  • Pour ajouter de nouvelles colonnes d'articles après le premier import, vous devrez recréer la base.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Fichiers acceptés</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li><strong>Formats:</strong> CSV, XLSX, XLS</li>
              <li><strong>Encodages CSV:</strong> utf-8, latin1, cp1252 (détection automatique)</li>
              <li><strong>Délimiteurs CSV:</strong> ; , tabulation, | (détection automatique)</li>
              <li><strong>Excel multi-feuilles:</strong> si plusieurs onglets, la première feuille est lue</li>
              <li><strong>Taille conseillée:</strong> ≤ 50 Mo</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Colonnes d'articles (requises)</h4>
            <div className="text-sm text-gray-700 space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="font-medium mb-2">Identifiant produit (clé unique)</div>
                <div className="text-xs text-gray-600 mb-2">En-têtes acceptés (casse/accents/espaces ignorés):</div>
                <div className="p-2 bg-white rounded border text-xs font-mono">ref, id, item_code, product_id, article_id, sku</div>
                <ul className="list-disc ml-5 mt-2 space-y-1 text-xs">
                  <li>Normalisation: suppression des caractères non alphanumériques; 101.0 → "101".</li>
                  <li>Doublons: la dernière occurrence l'emporte lors de la création.</li>
                </ul>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="font-medium mb-2">Nom du produit</div>
                <div className="text-xs text-gray-600 mb-2">En-têtes acceptés:</div>
                <div className="p-2 bg-white rounded border text-xs font-mono">nom, name, product_name, article_name, description, title</div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Colonnes de ventes (ignorées lors de l'import d'articles)</h4>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Note :</strong> Les colonnes de ventes (format MMM_AA comme janv_22) présentes dans votre fichier d'articles seront automatiquement ignorées. 
                Utilisez l'onglet "Données de Ventes" pour importer vos données de ventes séparément.
              </p>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Format: <strong>MMM_AA</strong> (janv_22, fevr_22, ...). Mois acceptés: janv, fevr, mars, avr, mai, juin, juil, aout, sept, oct, nov, dec.</li>
              <li>Ces colonnes seront ignorées lors de l'import d'articles et un avertissement vous sera affiché.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Colonnes d'attributs (optionnelles)</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Ajoutez librement des attributs (ex: description, categorie, prix_vente, couleur, matiere, event, collection...).</li>
              <li>Les noms sont sanitisés: minuscules, accents supprimés, espaces → "_". Exemple: "Prix de vente" → "prix_de_vente".</li>
              <li><strong>Important :</strong> Ces colonnes deviendront permanentes. Assurez-vous d'inclure toutes les colonnes que vous pourriez vouloir utiliser plus tard.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Exemples</h4>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600 mb-1">Exemple avec ventes (optionnel):</p>
                <div className="p-3 bg-gray-50 rounded border text-xs font-mono overflow-x-auto">
                  item_code,nom,description,categorie,prix_vente,janv_22,fevr_22,mars_22<br/>
                  10160,20 pinces en bois naturel 3cm,Description produit,Pinces,0.65,100,120,90<br/>
                  10161,30 pinces en bois naturel 5cm,Description produit,Pinces,0.85,80,95,110
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Exemple sans ventes (recommandé pour commencer):</p>
                <div className="p-3 bg-gray-50 rounded border text-xs font-mono overflow-x-auto">
                  item_code,nom,description,categorie,prix_vente,marque<br/>
                  10160,20 pinces en bois naturel 3cm,Description produit,Pinces,0.65,ArtCraft<br/>
                  10161,30 pinces en bois naturel 5cm,Description produit,Pinces,0.85,ArtCraft
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Rappels:</strong> Ce guide explique le format pour importer des articles dans une base existante. 
              Pour <strong>ajouter des articles</strong> à une base existante, utilisez l'onglet Articles; 
              pour <strong>ajouter/remplacer des ventes</strong>, utilisez l'onglet Données de Ventes.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Compris
          </button>
        </div>
      </div>
    </div>
  );
};

const MesDonnees: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const { activeDatabase } = useDatabaseContext();

  const handleDatabaseCreated = () => {
    setRefreshKey(prev => prev + 1);
    setShowCreateModal(false);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
  };

  return (
    <div className="bg-gray-50 flex flex-col" style={{ height: '92vh' }}>
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Database className="text-blue-600" size={32} />
                Gestion des Bases de Données
              </h1>
              <p className="mt-2 text-lg text-gray-600">
                Créez et gérez vos bases de données
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Add Database Button */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFormatModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Info size={16} />
                  Format CSV
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  <Plus size={20} />
                  Ajouter une nouvelle base
                </button>
              </div>
              
              <div className="hidden md:flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm text-gray-500">Base active</div>
                  <div className="font-semibold text-gray-900">
                    {activeDatabase?.name || 'Aucune'}
                  </div>
                </div>
                {activeDatabase && (
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-full">
          {/* Database List - Fixed Height with Scroll */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Database size={20} className="text-green-600" />
                Bases de Données Existantes
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Sélectionnez une base ou gérez son contenu
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <DatabaseList refreshTrigger={refreshKey} />
            </div>
          </div>
        </div>
      </div>

      {/* Create Database Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal if clicking on backdrop (not on modal content)
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Plus size={20} className="text-blue-600" />
                  Créer une Nouvelle Base de Données
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Créez une base de données vide
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Fermer"
                title="Fermer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <FileUpload onSuccess={handleDatabaseCreated} />
            </div>
          </div>
        </div>
      )}

      {/* Format Modal */}
      <DatabaseFormatModal isOpen={showFormatModal} onClose={() => setShowFormatModal(false)} />
    </div>
  );
};

export default MesDonnees;